import { Image } from '@/lib/next-shim'
import { useTranslations } from '@/lib/next-shim'

import logoGif from '@/assets/img/logo.gif'
import LinkButton from '@/components/external/material/LinkButton'
import Container from '@/components/ui/layouts/Container'
import { LanguageSwitcher } from '@/shared/ui/LanguageSwitcher'
import { AccountMenu } from '@/features/account-menu'

import { Link } from '@/lib/i18n/settings'
import { getEnv } from '@/lib/env'
import { shouldUseCustomBranding } from '@/utils/landing-variant'
import { scrollToSection } from '@/utils/scrollToSection'

type HeaderProps = {
  isAuthenticated?: boolean
  navMenu: Array<{
    href: string
    label: string
    name: string
  }>
}

export function Header(props: HeaderProps) {
  const { isAuthenticated, navMenu } = props
  const t = useTranslations()

  // Get branding information for white labeling
  const isCustomBranded = shouldUseCustomBranding()
  const brandLogo = getEnv('NEXT_PUBLIC_BRAND_LOGO')
  const brandName = getEnv('NEXT_PUBLIC_BRAND_NAME', 'Medconnect Platform')

  // Enhanced navigation with additional items (only when not custom branded)
  const enhancedNavMenu = !isCustomBranded
    ? [
        {
          name: 'how-it-works',
          label: 'How It Works',
          href: '#how-it-works',
        },
        {
          name: 'meeting-links',
          // Anchor to the on-page meeting-links section (like #how-it-works above); the bare
          // /meeting-links route is a redirect-to-home, so a path here would bounce back to /.
          label: 'Book Now',
          href: '#meeting-links',
        },
        ...navMenu,
      ]
    : []

  return (
    <header className="py-4 md:py-6 bg-white shadow-sm sticky top-0 z-50">
      <Container>
        <nav className="relative flex justify-between items-center">
          <div className="flex items-center md:gap-x-10">
            <Link href="/" aria-label="Home" className="flex">
              {isCustomBranded && brandLogo ? (
                <Image alt={brandName} src={brandLogo} priority width={107} height={60} className="h-16 w-auto object-contain" />
              ) : (
                <Image alt="logo" src={logoGif} priority width={107} height={60} className="h-16 w-auto" />
              )}
            </Link>
            {!isCustomBranded && (
              <div className="hidden md:flex md:items-center md:space-x-10">
                {enhancedNavMenu.map((item) => (
                  <a
                    key={item.name}
                    href={item.href}
                    onClick={
                      item.name === 'meeting-links'
                        ? (e) => {
                            e.preventDefault()
                            scrollToSection('meeting-links')
                          }
                        : undefined
                    }
                    className={`font-medium text-gray-700 hover:text-primary transition-colors ${
                      item.name === 'meeting-links' ? 'text-primary font-semibold' : ''
                    }`}
                  >
                    {item.label}
                  </a>
                ))}
              </div>
            )}
          </div>
          <div className="flex items-center gap-x-2 sm:gap-x-3">
            {isAuthenticated ? (
              <div data-testid="user-menu-button">
                <AccountMenu />
              </div>
            ) : (
              <>
                <LinkButton href="/login">{t('button.signIn.label')}</LinkButton>
                <LinkButton color="primary" disableElevation href="/register" variant="contained">
                  {t('button.signUp.label')}
                </LinkButton>
              </>
            )}
            <div className="hidden md:block" data-testid="language-selector">
              <LanguageSwitcher />
            </div>
          </div>
        </nav>
      </Container>
    </header>
  )
}
